import React, { useContext, useEffect, useState } from "react";
import clsx from "clsx";
import { navigate } from "gatsby";
import { useLanguageContext, useMatchMediaContext } from "@/context";
import Arrow from "@/assets/svg/single-post-arrow.svg";
import NewsImage from "@/assets/img/news.png";
import { ImageComponent } from "@/components/elements/Image";
import LinkComponent from "@/components/elements/Link";
import MarkdownComponent from "@/components/elements/Markdown";
import SocialShare from "@/components/elements/SocialShare";
import formatDate from "@/base/helpers/formatDate";
import { Storyblok } from "@/base/lib/storyblok";

import * as styles from "./styles.module.css";

interface Props {
  postContent: PostTemplate;
  path: string;
  postId?: number;
}

type PostsResult = {
  data: {
    stories: ParsedStoryblokStory<PostTemplate>[];
  };
};

const getAuthor = (
  authorString: string,
  authorMember: MemberType,
  authorProject: {
    uuid: string;
    name: string;
    content: PortfolioProjectType;
  }
) => {
  if (authorString && !!authorMember) {
    return `${authorString} | ${authorMember.name}`;
  }
  if (authorString && !!authorProject) {
    return `${authorString} | ${authorProject.name}`;
  }
  if (authorMember) return authorMember.name;

  if (authorProject) return authorProject.name;

  if (authorString) return authorString;
  return "Vertex SEA";
};

export default function CommonPostSection({
  postContent,
  path,
  postId,
}: Props) {
  const { isDesktop } = useMatchMediaContext();
  const {
    state: { language, DEFAULT_LANG },
  } = useContext(useLanguageContext);
  const {
    author,
    author_teamMember,
    post_author,
    post_content,
    post_date,
    post_hero,
    post_title,
  } = postContent;
  const [isLoading, setIsLoading] = useState(false);
  const [featuredNews, setFeaturedNews] = useState<
    ParsedStoryblokStory<PostTemplate>[]
  >([]);

  async function getAllFeaturedPosts() {
    setIsLoading(true);
    try {
      const { data }: PostsResult = await Storyblok.get(`cdn/stories`, {
        version: "published",
        cv: Date.now(),
        resolve_relations:
          "common_post.post_category, common_post.post_author, common_post.author_teamMember",
        per_page: 4,
        sort_by: "content.post_date:desc",
        excluding_ids: postId || "",
        starts_with: `${language}/news/`,
        content_type: "common_post",
      });
      setFeaturedNews(data.stories);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    getAllFeaturedPosts();
  }, []);

  const goBack = () => {
    if (!!window?.history?.state || !!window?.history?.state?.key) {
      navigate(-1);
    } else {
      navigate(language === DEFAULT_LANG ? "/news" : `/${language}/news`);
    }
  };

  return (
    <article className={styles.article_wrapper}>
      <div className="text-center lg:text-left xl:w-2/3">
        <div className="text-md text-secondary font-semibold font-fieldwork-hum leading-none">
          <button
            // url={language === DEFAULT_LANG ? "/news" : `/${language}/news`}
            className="mb-4 lg:mb-8 block"
            onClick={goBack}
          >
            <span className={clsx(styles.arrow, "lg:!hidden")}></span>
            <span className="hidden lg:inline">&lt;</span> Back to all News
          </button>
        </div>
        <div className="relative">
          <h1 className={styles.article_title}>{post_title}</h1>
          {isDesktop && (
            <div className="absolute -left-40 -bottom-20 2xl:-left-32">
              <img src={Arrow} alt="" />
            </div>
          )}
        </div>
        <span className="block mb-12 xl:mb-14 text-tiny lg:text-lg font-fieldwork-hum leading-none">
          {getAuthor(author, author_teamMember, post_author)} |{" "}
          {formatDate(post_date)}
        </span>
        <div id="post_image_marker"></div>
      </div>
      <div className="xl:flex">
        {post_hero && post_hero.image ? (
          <div className={styles.image__wrapper}>
            <ImageComponent
              fluid
              data={post_hero}
              options={{
                maxWidth: 950,
                quality: 90,
              }}
              style={{ borderRadius: "0.8rem", height: "100%" }}
            />
            {post_hero.copyright_info && (
              <p>
                <em>{post_hero.copyright_info}</em>
              </p>
            )}
          </div>
        ) : (
          <div className={styles.image__wrapper}>
            <img
              src={NewsImage}
              alt=""
              style={{ borderRadius: "0.8rem", height: "100%" }}
            />
          </div>
        )}
        {isDesktop && (
          <aside className="xl:w-1/3 xl:ml-32 xl:-mt-14">
            <p className="text-sm font-fieldwork-hum font-bold mb-7">
              You may also like...
            </p>
            <div className="mb-20">
              {isLoading ? (
                <p>Loading...</p>
              ) : (
                <ul>
                  {featuredNews.map((item) => {
                    const internalUrl =
                      language === DEFAULT_LANG
                        ? `/news/${item.slug}`
                        : `/${language}/news/${item.slug}`;

                    const newsUrl = item.content.external_post
                      ? item.content.external_link
                      : internalUrl;

                    return (
                      <li key={item.uuid}>
                        {newsUrl && (
                          <LinkComponent
                            url={newsUrl}
                            type={
                              item.content.external_post
                                ? "external"
                                : "internal"
                            }
                            className="flex items-center mb-8"
                          >
                            <div className="w-24">
                              {item.content.post_hero && (
                                <ImageComponent
                                  fluid
                                  data={item.content.post_hero}
                                  options={{
                                    maxWidth: 98,
                                    maxHeight: 98,
                                  }}
                                  style={{ borderRadius: "0.8rem" }}
                                />
                              )}
                            </div>
                            <div className="w-2/3 ml-8">
                              <span className="block text-tiny opacity-50 font-fieldwork-hum">
                                {item.content.post_author?.name ||
                                  item.content.author_teamMember.name ||
                                  item.content.author ||
                                  "Vertex SEA"}
                                {" | "}
                                {item.content.post_date &&
                                  formatDate(item.content.post_date)}
                              </span>
                              <p className="text-xs leading-5 font-fieldwork-hum">
                                {item.content.post_title}
                              </p>
                            </div>
                          </LinkComponent>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <SocialShare path={path} />
          </aside>
        )}
      </div>
      <div className="xl:w-2/3">
        <div
          className={clsx(
            "font-fieldwork-hum text-sm lg:text-lg",
            styles.post__content
          )}
        >
          {post_content && (
            <MarkdownComponent>{post_content}</MarkdownComponent>
          )}
        </div>
      </div>
    </article>
  );
}
